import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgTable from "../AgTable";
import { APPINSIGHTS_STRINGS } from "../../constants/appInsightsStrings";
import { trackEvent } from "../../utils/appInsights";
import { useTranslation } from "react-i18next";

import { deleteCip, getCIPs } from "../../network/cipAPI";
import { storeCIPS } from "../../Store/TemplateStore/template.action";

import DupontButton from "../DupontButton";
import AddCIPDialog from "../../pages/addCIP";
import DupontDialog from "../DupontDialog";
import { getSortedData } from "../../utils/tableDataFormatter";
import createNotification, {
  ERROR_NOTIFICATION,
  SUCCESS_NOTIFICATION,
} from "../Notifications";
import { USER_ADDED_TABLE_COLUMNS } from "./userAddedCipTable.config";
import PopoverText from "../PopoverText";
import InfoIconGreen from "../../assets/info-icon-green.svg";

const UserAddedCIP = ({
  fileInfo,
  min_date,
  max_date,
  fileId,
  setView,
  addCecButtonDisable,
}) => {
  const { t, i18n } = useTranslation();
  const gridRef = useRef(null);
  const dispatch = useDispatch();

  const [gridApi, setGridAPi] = useState(null);
  const [addDialogOpen, setAddDilogOpen] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [editedRow, setEditedRow] = useState(null);
  const userCips = useSelector((state) => state.cips);
  const temperature = useSelector((state) => state.temperature);
  const mass = useSelector((state) => state.mass);
  const [rowData, setRowData] = useState(
    userCips
      ? getSortedData(userCips.filter((item) => !item.is_tool_identified))
      : []
  );
  const [deletedRow, setDeletedRow] = useState(null);
  const [showDeletePopup, setShowDeletedDialog] = useState(false);
  const [updateLanguage, setUpdateLanguage] = useState(false);

  useEffect(() => {
    setUpdateLanguage((prev) => !prev);
  }, [i18n.language]);

  useEffect(() => {
    if (!fileId) {
      setRowData([]);
    }
  }, [fileId]);

  useEffect(() => {
    const fetchCips = async () => {
      try {
        const cips = await getCIPs();
        if (cips && cips?.data) {
          const userCips = cips?.data.filter(
            (item) => !item.is_tool_identified
          );
          setRowData(getSortedData(userCips));
        }
      } catch (err) { }
    };
    fetchCips();
  }, []);

  const deleteHandler = async (params) => {
    setDeletedRow(params);
    setShowDeletedDialog(true);
  };

  const editHandler = (params) => {
    setIsEdited(true);
    setAddDilogOpen(true);
    setEditedRow(params);
  };

  const [columnDefs, setColumnDefs] = useState(
    USER_ADDED_TABLE_COLUMNS(t, temperature, mass)
  );

  useEffect(() => {
    setColumnDefs(USER_ADDED_TABLE_COLUMNS(t, temperature, mass));
  }, [t, setColumnDefs]);

  const onGridReady = useCallback((params) => {
    let gridApi = params.api;
    setGridAPi(gridApi);
  });

  const onAddCIPClose = () => {
    setAddDilogOpen(false);
    setIsEdited(false);
    setEditedRow(null);
  };

  const onAddClick = () => {
    setAddDilogOpen(true);
  };

  const onProceedClicked = async (value) => {
    setShowDeletedDialog(false);
    if (value === "CONFIRM") {
      const deleteCips = await deleteCip(deletedRow.cip_id);
      if (deleteCips && deleteCips.status === 200) {
        // Track CIP deletion
        trackEvent(APPINSIGHTS_STRINGS.CIP_DELETED, {
          cipId: deletedRow.cip_id,
          type: deletedRow.type,
          startDate: deletedRow.start_date,
          endDate: deletedRow.end_date
        });

        createNotification(
          SUCCESS_NOTIFICATION,
          t("notificationStrings.deleteSuccess"),
          ""
        );
        setView(2);
        const cips = await getCIPs();
        if (cips.status === 200 && cips) {
          const filteredCips = cips?.data
            ? cips?.data.filter((item) => !item.is_tool_identified)
            : [];
          setRowData(filteredCips);
        }
      } else {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.deleteFail"),
          ""
        );
      }
    }
  };
  const updateStoreCips = (data) => {
    dispatch(storeCIPS([...data]));
  };

  return (
    <div>
      <div
        className="lightGrayBorder"
        style={{ padding: "16px", marginTop: "10px" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="headerTealText">{t("CIPShutdownStrings.title")}</div>
            <PopoverText
              descriptor={<img src={InfoIconGreen} alt="icon" />}
              descriptionText={t("CIPShutdownStrings.recordPastCIPsAndShutdowns")}
            />
          </div>
          <DupontButton
            customClass={{ margin: "auto" }}
            onClick={onAddClick}
            disabled={addCecButtonDisable}
          >
            {t("CIPShutdownStrings.addCip")}
          </DupontButton>
          {addDialogOpen && (
            <AddCIPDialog
              open={addDialogOpen}
              onClose={onAddCIPClose}
              rowData={rowData}
              setRowData={setRowData}
              isEdited={isEdited}
              editedRow={editedRow}
              gridAPI={gridApi}
              fileInfo={fileInfo}
              max_date={max_date}
              min_date={min_date}
              setOpen={setAddDilogOpen}
              updateStoreCips={updateStoreCips}
              isUSerAdded
              setView={setView}
            />
          )}
        </div>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", marginTop: "10px", overflow: "auto" }}
        >
          <AgTable
            key={updateLanguage}
            ref={gridRef} 
            columnDefs={columnDefs}
            rowData={rowData}
            onGridReady={onGridReady}
            context={{ deleteHandler, editHandler }}
            name={t("excelSheetNames.cipAndShutdown")}
          />
          {showDeletePopup && (
            <DupontDialog
              open={showDeletePopup}
              type="primary"
              title={t("notificationStrings.deleteConfirmationCIP")}
              cancelBtnText={t("ValueStrings.no")}
              confirmBtnText={t("ValueStrings.yes")}
              onClose={onProceedClicked}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAddedCIP;
